// One section of the console: a heading, an optional count, and the reason it is empty.
//
// Every admin payload carries `unavailable` — a sentence when Postgres could not be read, and
// null otherwise. It is rendered here rather than in each table because the distinction it
// draws is the same everywhere and is the whole point of the surface: "nothing happened" and
// "I could not look" must never render as the same empty table.

import type { ReactNode } from 'react';
import { SectionHead } from '../components/SectionHead';

export function AdminSection({
  title,
  count,
  asOf,
  loading = false,
  unavailable,
  children,
}: {
  title: string;
  count?: string | null;
  asOf?: string | null;
  // True while the first fetch is in flight. Rendered before `unavailable` and before the
  // children, because a table's own empty state ("no knock matches") reads as an answer and
  // showing it during the load is the third way to say "nothing happened" wrongly.
  loading?: boolean;
  unavailable?: string | null;
  children: ReactNode;
}) {
  return (
    <section className="adminsection">
      <SectionHead title={title} count={count} asOf={asOf} />
      {loading ? <p className="adminloading">Reading Postgres…</p> : null}
      {!loading && unavailable ? <p className="adminunavailable">{unavailable}</p> : null}
      {!loading && !unavailable ? children : null}
    </section>
  );
}

// A state, in colour and in a word. Semantic tone only — never the accent hue, which says
// "this is Cartesia", not "this is fine".
export function StatePill({
  tone,
  label,
}: {
  tone: 'ok' | 'warn' | 'bad';
  label: string;
}) {
  return <span className={'statepill ' + tone}>{label}</span>;
}
